import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { AlertCircle } from 'lucide-react';

interface ReportDelayDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: { estimated_delay_minutes: number; delay_reason?: string }) => void;
  isSubmitting: boolean;
}

export const ReportDelayDialog = ({
  open,
  onOpenChange,
  onSubmit,
  isSubmitting,
}: ReportDelayDialogProps) => {
  const [delayOption, setDelayOption] = useState<string>('');
  const [customDelay, setCustomDelay] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [error, setError] = useState<string>('');

  const handleSubmit = () => {
    setError('');

    let delayMinutes: number;

    if (delayOption === 'custom') {
      const customMinutes = parseInt(customDelay);
      if (isNaN(customMinutes) || customMinutes < 5 || customMinutes > 480) {
        setError('Le délai doit être entre 5 et 480 minutes (8 heures maximum)');
        return;
      }
      delayMinutes = customMinutes;
    } else {
      delayMinutes = parseInt(delayOption);
      if (isNaN(delayMinutes)) {
        setError('Veuillez sélectionner un délai');
        return;
      }
    }

    onSubmit({
      estimated_delay_minutes: delayMinutes,
      delay_reason: reason.trim() || undefined,
    });

    // Reset form
    setDelayOption('');
    setCustomDelay('');
    setReason('');
    setError('');
  };

  const handleCancel = () => {
    setDelayOption('');
    setCustomDelay('');
    setReason('');
    setError('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-[#37699E] font-['Lexend_Deca']">
            Signaler un retard
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="delay" className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide">
              Délai estimé *
            </Label>
            <Select value={delayOption} onValueChange={setDelayOption}>
              <SelectTrigger
                id="delay"
                className="w-full border-[#DBE2EA] focus:border-[#0880AE] focus:ring-2 focus:ring-[#0880AE]/20"
              >
                <SelectValue placeholder="Sélectionnez un délai" />
              </SelectTrigger>
              <SelectContent className="bg-white z-50">
                <SelectItem value="15">15 minutes</SelectItem>
                <SelectItem value="30">30 minutes</SelectItem>
                <SelectItem value="45">45 minutes</SelectItem>
                <SelectItem value="60">1 heure</SelectItem>
                <SelectItem value="90">1h30</SelectItem>
                <SelectItem value="120">2 heures</SelectItem>
                <SelectItem value="180">3 heures</SelectItem>
                <SelectItem value="240">4 heures</SelectItem>
                <SelectItem value="custom">Autre (personnalisé)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {delayOption === 'custom' && (
            <div className="space-y-2">
              <Label htmlFor="customDelay" className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide">
                Délai personnalisé (minutes) *
              </Label>
              <Input
                id="customDelay"
                type="number"
                min="5"
                max="480"
                placeholder="Ex: 90"
                value={customDelay}
                onChange={(e) => setCustomDelay(e.target.value)}
                className="border-[#DBE2EA] focus:border-[#0880AE] focus:ring-2 focus:ring-[#0880AE]/20"
              />
              <p className="text-xs text-[#5A6B7A]">Entre 5 et 480 minutes (8h max)</p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="reason" className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide">
              Raison (optionnel)
            </Label>
            <Textarea
              id="reason"
              placeholder="Ex: Embouteillages sur A1"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
              rows={3}
              className="border-[#DBE2EA] focus:border-[#0880AE] focus:ring-2 focus:ring-[#0880AE]/20 resize-none"
            />
            <p className="text-xs text-[#5A6B7A] text-right">{reason.length}/500</p>
          </div>

          {error && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-[#FF7171]/10 border border-[#FF7171]/20">
              <AlertCircle className="text-[#FF7171] flex-shrink-0 mt-0.5" size={16} />
              <p className="text-sm text-[#FF7171]">{error}</p>
            </div>
          )}
        </div>

        <div className="flex gap-3 justify-end">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isSubmitting}
            className="border-[#DBE2EA] text-[#2D3748] hover:bg-[#EBF4F8]"
          >
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || !delayOption || (delayOption === 'custom' && !customDelay)}
            className="bg-[#F2AC57] hover:bg-[#D69440] text-white"
          >
            {isSubmitting ? 'Envoi...' : 'Confirmer'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
